<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import CampoDataIntervalo from '@/components/base/campos/CampoDataIntervalo.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import { formatarDataBR, formatarDataHoraBR } from '@/utils/useDataBR'
import { useUI } from '@/composables/useUI'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['cancelar', 'show-notification'])

const metadataStore = useMetadataListasStore()
const { obterBadgeNeutro } = useUI()

// --- Estado ---
const loading = ref(false)
const limite = ref({})
const exposicoes = ref([])
const listas = reactive({ moedas: [] })

// --- Filtros ---
const filtroDataInicio = ref('')
const filtroDataFim = ref('')
const filtroTipo = ref('todas') // 'todas' | 'contratacao' | 'liquidacao'
const filtroVisao = ref('operacoes') // 'operacoes' | 'sequencial'
const mostrarMoedaExtra = ref(false)

// --- Helpers de formatação ---
const formatarNumero = (valor, decimais = 2) => {
  if (valor == null || valor === '') return '-'
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais
  }).format(Number(valor))
}

const formatarMoeda = (valor) => {
  if (valor == null || valor === '') return '-'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(valor))
}

// --- Dados do limite (limite recebido via props) ---
const item = computed(() => props.itemParaEditar || {})

const saldoDisponivel = computed(() => {
  if (!limite.value.valorAprovado || limite.value.saldoUtilizado == null) return 0
  return Number(limite.value.valorAprovado) - Number(limite.value.saldoUtilizado)
})

const siglaMoeda = computed(() => {
  if (!limite.value.lstMoeda || !listas.moedas.length) return limite.value.lstMoeda || '-'
  const moeda = listas.moedas.find(m => String(m.idListaSistemaItem) === String(limite.value.lstMoeda) || String(m.idListaClienteItem) === String(limite.value.lstMoeda))
  return moeda?.textoLista || limite.value.lstMoeda
})

// --- Tradução de tipo de movimento ---
const textoMovimento = (lstTipoMovimento) => {
  if (lstTipoMovimento === 'TP_MOVIMENTO1' || lstTipoMovimento === 'ContratacaoCredito') return 'Contratação'
  if (lstTipoMovimento === 'TP_MOVIMENTO2' || lstTipoMovimento === 'LiquidacaoCredito') return 'Liquidação'
  return lstTipoMovimento || '-'
}

const classeMovimento = (lstTipoMovimento) => {
  if (lstTipoMovimento === 'TP_MOVIMENTO1' || lstTipoMovimento === 'ContratacaoCredito') return 'badge bg-success'
  if (lstTipoMovimento === 'TP_MOVIMENTO2' || lstTipoMovimento === 'LiquidacaoCredito') return 'badge bg-danger'
  return obterBadgeNeutro()
}

const iconeMovimento = (lstTipoMovimento) => {
  if (lstTipoMovimento === 'TP_MOVIMENTO1' || lstTipoMovimento === 'ContratacaoCredito') return 'bi-arrow-up-circle text-success'
  if (lstTipoMovimento === 'TP_MOVIMENTO2' || lstTipoMovimento === 'LiquidacaoCredito') return 'bi-arrow-down-circle text-danger'
  return 'bi-circle'
}

// --- Exposições filtradas ---
const exposicoesFiltradas = computed(() => {
  let lista = [...exposicoes.value]

  // Filtro por tipo
  if (filtroTipo.value === 'contratacao') {
    lista = lista.filter(e => e.lstTipoMovimento === 'TP_MOVIMENTO1' || e.lstTipoMovimento === 'ContratacaoCredito')
  } else if (filtroTipo.value === 'liquidacao') {
    lista = lista.filter(e => e.lstTipoMovimento === 'TP_MOVIMENTO2' || e.lstTipoMovimento === 'LiquidacaoCredito')
  }

  // Filtro por período
  if (filtroDataInicio.value) {
    const dataInicio = filtroDataInicio.value
    lista = lista.filter(e => {
      const dataMov = e.tsCriacao ? e.tsCriacao.split('T')[0] : ''
      return dataMov >= dataInicio
    })
  }
  if (filtroDataFim.value) {
    const dataFim = filtroDataFim.value
    lista = lista.filter(e => {
      const dataMov = e.tsCriacao ? e.tsCriacao.split('T')[0] : ''
      return dataMov <= dataFim
    })
  }

  return lista
})

// --- Carregar dados ---
const carregarDados = async () => {
  if (!item.value.idLimiteCredito) return

  loading.value = true
  try {
    const response = await api.get(`/LimitesCredito/${item.value.idLimiteCredito}/extrato`)
    limite.value = response.data.limite
    exposicoes.value = response.data.exposicoes || []
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao carregar extrato do limite.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    loading.value = false
  }
}

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['LimiteCredito', 'LimiteCreditoExposicao'])
    listas.moedas = result.LstMoeda || []
  } catch (e) {
    console.warn('Erro ao carregar listas:', e)
  }
}

const handleFechar = () => {
  emit('cancelar')
}

onMounted(async () => {
  await carregarListas()
  await carregarDados()
})
</script>

<template>
  <div class="limite-extrato-wrapper">
    <!-- ============================================================ -->
    <!-- CABEÇALHO                                                     -->
    <!-- ============================================================ -->
    <header class="relatorio-header">
      <div class="titulo-wrapper">
        <h4 class="titulo-principal">Extrato do Limite de Crédito</h4>
        <span class="titulo-acao">{{ limite.descricao || 'Sem descrição' }}</span>
      </div>

      <div class="info-registro">
        <div class="info-item">
          <strong>ID:</strong>
          <span class="info-valor">#{{ limite.idLimiteCredito }}</span>
        </div>
        <div class="info-item">
          <strong>Contrato:</strong>
          <span class="info-valor">{{ limite.numeroContrato || '-' }}</span>
        </div>
      </div>

      <div class="botoes-wrapper">
        <button type="button" class="btn btn-secondary" @click="handleFechar">
          <i class="bi bi-x-lg me-1"></i> Fechar
        </button>
      </div>
    </header>

    <!-- ============================================================ -->
    <!-- CARDS RESUMO DO LIMITE                                        -->
    <!-- ============================================================ -->
    <div class="resumo-executivo-row mb-4">
      <div class="resumo-card">
        <span class="resumo-label">Valor Aprovado</span>
        <span class="campo-valor destaque text-primary-emphasis">
          {{ formatarMoeda(limite.valorAprovado) }}
          <small class="text-body-secondary"> {{ siglaMoeda }}</small>
        </span>
      </div>
      <div class="resumo-card">
        <span class="resumo-label">Saldo Utilizado</span>
        <span class="campo-valor destaque text-warning-emphasis">
          {{ formatarMoeda(limite.saldoUtilizado) }}
          <small class="text-body-secondary"> {{ siglaMoeda }}</small>
        </span>
      </div>
      <div class="resumo-card">
        <span class="resumo-label">Saldo Disponível</span>
        <span class="campo-valor destaque" :class="saldoDisponivel >= 0 ? 'text-success-emphasis' : 'text-danger-emphasis'">
          {{ formatarMoeda(saldoDisponivel) }}
          <small class="text-body-secondary"> {{ siglaMoeda }}</small>
        </span>
      </div>
      <div class="resumo-card">
        <span class="resumo-label">Validade</span>
        <span class="campo-valor destaque">{{ limite.dataValidade ? formatarDataBR(limite.dataValidade) : '-' }}</span>
      </div>
      <div class="resumo-card">
        <span class="resumo-label">Rotativo</span>
        <span class="campo-valor destaque">
          <span v-if="limite.isRotativa" class="badge text-primary-emphasis bg-primary-subtle border border-primary-subtle px-3">Sim</span>
          <span v-else class="badge text-secondary-emphasis bg-secondary-subtle border border-secondary-subtle px-3">Não</span>
        </span>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- FILTROS                                                        -->
    <!-- ============================================================ -->
    <div class="filtros-section mb-3">
      <div class="row g-3 align-items-end">
        <div class="col-md-3">
          <CampoDataIntervalo
            id="filtroPeriodo"
            label="Período"
            :model-value-inicio="filtroDataInicio"
            :model-value-fim="filtroDataFim"
            @update:model-value-inicio="filtroDataInicio = $event"
            @update:model-value-fim="filtroDataFim = $event"
          />
        </div>
        <div class="col-md-2">
          <CampoSelect
            id="filtroTipo"
            label="Tipo"
            :options="[
              { value: 'todas', label: 'Todas' },
              { value: 'contratacao', label: 'Contratação' },
              { value: 'liquidacao', label: 'Liquidação' }
            ]"
            option-value="value"
            option-label="label"
            v-model="filtroTipo"
          />
        </div>
        <div class="col-md-2">
          <CampoSelect
            id="filtroVisao"
            label="Visualização"
            :options="[
              { value: 'operacoes', label: 'Apenas Operações' },
              { value: 'sequencial', label: 'Sequencial (c/ saldos)' }
            ]"
            option-value="value"
            option-label="label"
            v-model="filtroVisao"
          />
        </div>
        <div class="col-md-2 d-flex align-items-center pb-2">
          <div class="form-check">
            <input
              type="checkbox"
              class="form-check-input"
              id="mostrarMoedaExtra"
              v-model="mostrarMoedaExtra"
            />
            <label class="form-check-label" for="mostrarMoedaExtra">
              Mostrar BRL/USD
            </label>
          </div>
        </div>
        <div class="col-md-auto ms-auto pb-2">
          <span class="text-body-secondary small">
            {{ exposicoesFiltradas.length }} registro(s)
          </span>
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- TABELA DE EXTRATO                                              -->
    <!-- ============================================================ -->
    <div class="relatorio-body">
      <div v-if="loading" class="text-center p-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
      </div>

      <div v-else-if="exposicoesFiltradas.length === 0" class="text-center p-5 text-body-secondary">
        <i class="bi bi-inbox fs-1 d-block mb-2"></i>
        <p>Nenhum registro de exposição encontrado para o período selecionado.</p>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-hover align-middle mb-0 extrato-table">
          <thead class="table-dark">
            <tr>
              <th class="text-nowrap" style="width: 160px;">Data / Hora</th>
              <th class="text-nowrap" style="width: 130px;">Tipo</th>
              <th class="text-nowrap">Operação</th>
              <th class="text-nowrap text-end" style="width: 150px;">Valor ({{ siglaMoeda }})</th>
              <th class="text-nowrap text-end" style="width: 150px;">Saldo Antes</th>
              <th class="text-nowrap text-end" style="width: 150px;">Saldo Depois</th>
              <!-- Colunas extras BRL/USD -->
              <template v-if="mostrarMoedaExtra">
                <th class="text-nowrap text-end" style="width: 130px;">Valor BRL</th>
                <th class="text-nowrap text-end" style="width: 130px;">Saldo Antes BRL</th>
                <th class="text-nowrap text-end" style="width: 130px;">Saldo Depois BRL</th>
                <th class="text-nowrap text-end" style="width: 130px;">Valor USD</th>
                <th class="text-nowrap text-end" style="width: 130px;">Saldo Antes USD</th>
                <th class="text-nowrap text-end" style="width: 130px;">Saldo Depois USD</th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-for="exp in exposicoesFiltradas" :key="exp.idLimiteCreditoExposicao">
              <!-- Data / Hora -->
              <td class="text-nowrap small text-body-secondary">
                {{ formatarDataHoraBR(exp.tsCriacao) }}
              </td>

              <!-- Tipo (badge) -->
              <td>
                <span :class="classeMovimento(exp.lstTipoMovimento)" class="d-inline-flex align-items-center gap-1">
                  <i :class="iconeMovimento(exp.lstTipoMovimento)" class="small"></i>
                  {{ textoMovimento(exp.lstTipoMovimento) }}
                </span>
              </td>

              <!-- Operação -->
              <td class="text-nowrap">
                <span class="fw-medium">{{ exp.lstTipoOperacao }}</span>
                <span class="text-body-secondary"> #{{ exp.idOperacao }}</span>
              </td>

              <!-- Valor Moeda Limite -->
              <td class="text-end fw-medium">
                {{ formatarNumero(exp.valorMoedaLimite) }}
              </td>

              <!-- Saldo Antes -->
              <td class="text-end" :class="exp.valorPreMovimentoLimite >= 0 ? '' : 'text-danger'">
                {{ formatarNumero(exp.valorPreMovimentoLimite) }}
              </td>

              <!-- Saldo Depois -->
              <td class="text-end fw-semibold" :class="exp.valorPosMovimentoLimite >= 0 ? '' : 'text-danger'">
                {{ formatarNumero(exp.valorPosMovimentoLimite) }}
              </td>

              <!-- Colunas extras BRL/USD -->
              <template v-if="mostrarMoedaExtra">
                <td class="text-end">{{ formatarNumero(exp.valorMoedaPadrao) }}</td>
                <td class="text-end">{{ formatarNumero(exp.valorPreMovimentoPadrao) }}</td>
                <td class="text-end">{{ formatarNumero(exp.valorPosMovimentoPadrao) }}</td>
                <td class="text-end">{{ exp.valorMoedaGerencial != null ? formatarNumero(exp.valorMoedaGerencial) : '-' }}</td>
                <td class="text-end">{{ exp.valorPreMovimentoGerencial != null ? formatarNumero(exp.valorPreMovimentoGerencial) : '-' }}</td>
                <td class="text-end">{{ exp.valorPosMovimentoGerencial != null ? formatarNumero(exp.valorPosMovimentoGerencial) : '-' }}</td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.limite-extrato-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.relatorio-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background-color: var(--bs-body-bg);
  border-bottom: 1px solid var(--bs-border-color);
  position: sticky;
  top: 0;
  z-index: 10;
  flex-wrap: wrap;
}

.titulo-wrapper {
  flex: 1;
  min-width: 200px;
}

.titulo-wrapper h4 {
  margin: 0;
  font-size: 1.1rem;
}

.titulo-acao {
  font-size: 0.8rem;
  color: var(--bs-secondary-color);
}

.info-registro {
  display: flex;
  gap: 1.5rem;
  font-size: 0.85rem;
  flex-wrap: wrap;
}

.info-item strong {
  margin-right: 0.25rem;
  color: var(--bs-secondary-color);
}

.botoes-wrapper {
  white-space: nowrap;
}

.relatorio-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

.resumo-executivo-row {
  display: flex;
  gap: 1rem;
  padding: 0 1rem;
  flex-wrap: wrap;
}

.resumo-card {
  flex: 1;
  min-width: 160px;
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.resumo-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--bs-secondary-color);
}

.campo-valor {
  font-size: 1.1rem;
  font-weight: 600;
}

.campo-valor small {
  font-weight: 400;
  font-size: 0.8rem;
}

.filtros-section {
  padding: 0 1rem;
}

.extrato-table th {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  white-space: nowrap;
}

.extrato-table td {
  font-size: 0.875rem;
  vertical-align: middle;
}

/* Responsivo */
@media (max-width: 991.98px) {
  .relatorio-header {
    position: static;
  }

  .resumo-executivo-row {
    flex-direction: column;
  }

  .resumo-card {
    min-width: 100%;
  }
}
</style>
